type RemoteLoadFailure = {
  remote: string;
  version: string;
  entry: string;
  release: string;
  lifecycle?: string;
  error: unknown;
};

export type ErrorReporter = (error: Error, data?: Record<string, unknown>) => Promise<void> | void;

const REASONS = ['missing', 'timeout', 'error'] as const;

const FETCH_FAILURES = ['Failed to fetch', 'Load failed', 'NetworkError', 'error loading remote'];

export class PinnedRemoteUnavailable extends Error {
  constructor(remote: string, version: string) {
    super(`The ${remote} remote did not come back at ${version}, the version its release pinned`);
    this.name = 'PinnedRemoteUnavailable';
  }
}

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error));

const reasonOf = (message: string) => REASONS.find((reason) => message.includes(`(${reason}:`)) ?? 'unknown';

const assetOf = (message: string) => message.match(/\((?:missing|timeout|error): ([^)\s]+)/)?.[1];

const chunkOf = (message: string) => message.match(/chunk (\S+?) failed/)?.[1];

export const isRemoteUnreachable = (error: unknown) => {
  if (error instanceof Error && error.name === 'ChunkLoadError') {
    return true;
  }

  const message = messageOf(error);

  return reasonOf(message) !== 'unknown' || FETCH_FAILURES.some((failure) => message.includes(failure));
};

let report: ErrorReporter | undefined;

export const reportRemoteLoadFailuresWith = (reporter: ErrorReporter) => {
  report = reporter;
};

export const resetErrorReporter = () => {
  report = undefined;
};

const reportRemoteLoadFailure = async ({ remote, version, entry, release, lifecycle, error }: RemoteLoadFailure) => {
  if (!report) {
    return;
  }

  const message = messageOf(error);

  await report(new PinnedRemoteUnavailable(remote, version), {
    remote,
    version,
    entry,
    release,
    host: environment.BUILD_IDENTIFIER,
    lifecycle,
    reason: reasonOf(message),
    asset: assetOf(message),
    chunk: chunkOf(message),
    cause: message,
  });
};

export default reportRemoteLoadFailure;
